import React, { FunctionComponent, useState, useRef } from "react";
import {
  Speaker,
  SkipForward,
  SkipBack,
  Play,
  Pause,
  Gear,
  XSmall,
} from "@vitalsource/vstui-icons";
import ExpandingLozenge from "../../common/ExpandingLozenge";
import styled, { keyframes } from "styled-components";
import { Button } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import { TTSProps } from "./withTTS";
import SettingsContent from "./SettingsContent";
import { Popover } from "@vitalsource/vstui/labs";
import { useIntl } from "react-intl";
import { POPOVER_V_OFFSET } from "../consts";
import { cloud } from "@vitalsource/vstui/colors";
import { convertSpacesToNonBreakingSpaces } from "../../../utils/string";
import RoundBorderButton from "components/common/RoundButton/RoundBorderButton";

const BASE_SIZE = 46;
const BUTTON_SIZE = 36;
const EXPANDED_SIZE = 200;

enum MenuTransition {
  OPEN = 1,
  OPENING = 2,
  CLOSING = 3,
  CLOSED = 4,
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Styled = {
  ControlContainer: styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 6px;
    align-items: center;
    border-radius: 25px;
    [aria-expanded="true"] {
      button {
        border-radius: 50%;
        border: 2px solid ${p => p.theme.vstui.color.primary.getShade(6)};
        background: ${(p: any) => p.theme.vstui.color.primary.getShade(2)};
      }
    }
  `,
  ButtonContainer: styled.div<{ large: boolean }>`
    position: relative;
    width: ${(EXPANDED_SIZE - BASE_SIZE * 2) / 3 / 10}rem;
    button {
      &:focus:hover {
        background: ${(p: any) =>
          p.theme.vstui.color.primary.getShade(2)} !important;
      }
    }

    &:first-child,
    &:last-child {
      width: ${BUTTON_SIZE / 10}rem;
    }
    &:not(:last-child) {
      animation: ${fadeIn} 0.1s;
    }
    svg {
      transform: scale(${props => (props.large ? 1.4 : 1)});
      transform-origin: center center;
    }
  `,
};

const ThemedButton = createThemedComponent(Button, {
  Button_borderRadius: "0px",
  Button_borderStyle: "solid",
  Button_borderWidth: "2px",
  Button_minHeight_medium: 35,

  Button_backgroundColor_neutralNaked_active: (p: any) =>
    p.theme.vstui.color.primary.getShade(2),
  Button_borderColor_neutralNaked_active: (p: any) =>
    p.theme.vstui.color.primary.getShade(6),
  Button_borderColor_neutralNaked_focus: (p: any) =>
    `${p.theme.vstui.color.primary.getShade(6)} !important`,
  Button_backgroundColor_neutralNaked_focus: (p: any) =>
    p.theme.vstui.color.primary.getShade(2),
  Button_boxShadow_neutralNaked_focus: `0 0 0 2px ${cloud.getShade(1)}`,
});

const ThemedRoundButton = createThemedComponent(ThemedButton, {
  Button_borderRadius: "100%",
  Button_minHeight_medium: 35,
  Button_backgroundColor_neutralNaked: cloud.getShade(1),
});

const TtsControlButton: FunctionComponent<{
  onClick?: () => void;
  label: string;
  disabled?: boolean;
  large?: boolean;
  round?: boolean;
  innerRef?: any;
}> = ({
  onClick,
  label,
  children,
  disabled,
  large = false,
  round = false,
  innerRef = { current: null },
}) => {
  const ButtonToUse = round ? ThemedRoundButton : ThemedButton;

  return (
    <Styled.ButtonContainer innerRef={innerRef} large={large}>
      <ButtonToUse
        onClick={onClick}
        naked
        color="neutral"
        iconStart={children}
        fullWidth
        label={convertSpacesToNonBreakingSpaces(label)}
        disabled={disabled}
      />
    </Styled.ButtonContainer>
  );
};

const SpeakerMenu = (ttsProps: TTSProps) => {
  const [menuStatus, setMenuStatus] = useState(MenuTransition.CLOSED);
  const intl = useIntl();
  let playButtonRef = useRef();
  let speakerButtonRef = React.useRef<any>(null);

  if (!ttsProps.isSupported) {
    return null;
  }

  if (menuStatus === MenuTransition.CLOSED) {
    return (
      <RoundBorderButton
        naked
        color="neutral"
        innerRef={speakerButtonRef}
        label={intl.formatMessage({ id: "tts.readAloud" })}
        onClick={() => {
          ttsProps.cancel();
          setMenuStatus(MenuTransition.OPENING);
        }}
        iconStart={<Speaker />}
        padding={10}
      />
    );
  }

  return (
    <ExpandingLozenge
      isOpen={
        menuStatus === MenuTransition.OPEN ||
        menuStatus === MenuTransition.OPENING
      }
      expandedWidth={EXPANDED_SIZE}
      height={BASE_SIZE}
      onCloseComplete={() => {
        setMenuStatus(MenuTransition.CLOSED);

        if (speakerButtonRef.current) {
          let speakerButton = speakerButtonRef.current.props.className;
          speakerButton = document.getElementsByClassName(speakerButton);
          if (speakerButton[0].children) {
            const els = speakerButton as any;
            els[0].children[0].focus();
          }
        }
      }}
      onOpenComplete={() => {
        setMenuStatus(MenuTransition.OPEN);
        if (playButtonRef.current) {
          const vstuiButton = (playButtonRef.current as any).getElementsByTagName(
            "button"
          )[0];
          vstuiButton && vstuiButton.focus();
        }
      }}
      role="toolbar"
      aria-label={intl.formatMessage({ id: "tts.toolbar" })}
    >
      <Styled.ControlContainer>
        {menuStatus === MenuTransition.OPEN && (
          <>
            <Popover
              modifiers={{
                offset: {
                  offset: `0, ${POPOVER_V_OFFSET}px`,
                },
              }}
              content={
                <SettingsContent
                  voices={ttsProps.voices}
                  currentVoice={ttsProps.currentVoice}
                  currentRate={ttsProps.currentRate}
                  altEnabled={ttsProps.altEnabled}
                  setVoice={ttsProps.setVoice}
                  changeRate={ttsProps.changeRate}
                  enableAltText={ttsProps.enableAltText}
                  disableAltText={ttsProps.disableAltText}
                />
              }
            >
              <TtsControlButton
                label={intl.formatMessage({ id: "general.settings" })}
                disabled={!ttsProps.isReady}
                round
              >
                <Gear />
              </TtsControlButton>
            </Popover>
            <TtsControlButton
              label={intl.formatMessage({ id: "tts.skipBack" })}
              onClick={ttsProps.skipBack}
              disabled={!ttsProps.isReady || !ttsProps.canNav}
            >
              <SkipBack />
            </TtsControlButton>
            {ttsProps.isPlaying ? (
              <TtsControlButton
                label={intl.formatMessage({ id: "general.pause" })}
                onClick={ttsProps.pause}
                disabled={!ttsProps.isReady}
                large
              >
                <Pause />
              </TtsControlButton>
            ) : (
              <TtsControlButton
                label={intl.formatMessage({ id: "general.play" })}
                onClick={ttsProps.play}
                disabled={!ttsProps.isReady}
                innerRef={playButtonRef}
                large
              >
                <Play />
              </TtsControlButton>
            )}
            <TtsControlButton
              label={intl.formatMessage({ id: "tts.skipForward" })}
              onClick={ttsProps.skipForward}
              disabled={!ttsProps.isReady || !ttsProps.canNav}
            >
              <SkipForward />
            </TtsControlButton>
          </>
        )}
        {menuStatus === MenuTransition.OPEN && (
          <TtsControlButton
            label={intl.formatMessage({ id: "general.close" })}
            onClick={() => {
              ttsProps.cancel();
              setMenuStatus(MenuTransition.CLOSING);
            }}
            round
          >
            <XSmall />
          </TtsControlButton>
        )}
      </Styled.ControlContainer>
    </ExpandingLozenge>
  );
};

export default SpeakerMenu;
